import React from "react";
import { Maximize2 } from "lucide-react";
import { AccordionV2 } from "../../../../components/ui/accordion/v2/Accordion";
import ManagementModal from "../../../../components/ui/modals/ManagementModal";
import ButtonV2 from "../../../../components/ui/button/v2";

/** Small count pill shown next to a section title (e.g. "Loan Accounts (2)"). */
export const CountBadge: React.FC<{ count: number }> = ({ count }) =>
  count ? (
    <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#EBEEFE] text-[#304ADD] text-xs font-medium">
      {count}
    </span>
  ) : null;

/** Red "N incomplete" pill shown on a section header after a failed submit. */
export const IncompleteBadge: React.FC<{ count: number }> = ({ count }) =>
  count ? (
    <span className="inline-flex items-center h-5 px-2 rounded-full bg-red-50 text-red-600 text-xs font-medium">
      {count} incomplete
    </span>
  ) : null;

interface ViewMoreButtonProps {
  overflowCount: number;
  singular: string;
  plural: string;
  invalidCount?: number;
  onClick: () => void;
}

export const ViewMoreButton: React.FC<ViewMoreButtonProps> = ({
  overflowCount,
  singular,
  plural,
  invalidCount = 0,
  onClick,
}) =>
  overflowCount > 0 ? (
    <ButtonV2 variant="outline" size="sm" onClick={onClick} className="h-8 gap-2">
      View {overflowCount} more {overflowCount === 1 ? singular : plural}
      {invalidCount > 0 && (
        <span className="inline-flex items-center h-4 px-1.5 rounded-full bg-red-50 text-red-600 text-[11px] font-medium">
          {invalidCount}
        </span>
      )}
    </ButtonV2>
  ) : null;

interface SectionAccordionProps {
  title: string;
  count: number;
  invalidCount?: number;
  onExpand?: () => void;
  rightSlot?: React.ReactNode;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

/** Section shell = AccordionV2 + count badge + optional maximize/rightSlot. */
export const SectionAccordion: React.FC<SectionAccordionProps> = ({
  title,
  count,
  invalidCount = 0,
  onExpand,
  rightSlot,
  defaultExpanded = true,
  children,
}) => (
  <AccordionV2
    expanded={defaultExpanded}
    className="mb-4"
    contentClassName="p-4"
    header={{
      title: (
        <span className="flex items-center gap-2 text-sm font-medium">
          {title} <CountBadge count={count} />
          <IncompleteBadge count={invalidCount} />
        </span>
      ),
      showArrowIcon: true,
      rightSlot: (
        <div className="flex items-center gap-2">
          {rightSlot}
          {onExpand && (
            <button
              type="button"
              className="p-1 text-gray-500 hover:text-gray-700"
              onClick={onExpand}
              title={`View all ${title.toLowerCase()}`}
            >
              <Maximize2 width={16} height={16} />
            </button>
          )}
        </div>
      ),
    }}
  >
    {children}
  </AccordionV2>
);

interface CollectionModalProps {
  title: string;
  onClose: () => void;
  onDone: () => void;
  children: React.ReactNode;
}

/** Wide, responsive "view all rows" modal — wraps the shared ManagementModal. */
export const CollectionModal: React.FC<CollectionModalProps> = ({
  title,
  onClose,
  onDone,
  children,
}) => (
  <ManagementModal
    title={title}
    submitLabel="Done"
    onSubmit={onDone}
    onClose={onClose}
    className="w-[92vw] max-w-[1100px]"
  >
    {children}
  </ManagementModal>
);
